import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import {
  DollarSign,
  ShoppingBag,
  Package,
  Users,
  ArrowRight,
  Plus,
  Loader2,
  AlertTriangle,
  TrendingUp,
  Clock,
  CheckCircle2,
  CreditCard,
  Send,
  Activity,
  ImageOff,
  TagsIcon,
  XCircle,
  Eye,
  Filter,
} from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'

type Color = 'emerald' | 'red' | 'blue' | 'violet' | 'amber' | 'sky' | 'pink' | 'indigo'

interface DashboardData {
  stats: {
    productos: number
    productosInactivos: number
    pedidos: number
    usuarios: number
    newsletter: number
    newsletterLastWeek: number
    ventasTotal: number
    ventasHoy: number
    pedidosHoy: number
    ventas7: number
    pedidos7: number
    ventas30: number
    pedidos30: number
    ticketPromedio: number
    pendientes: number
    pendientesUrgentes: number
    preparando: number
    enviadosHoy: number
    sinFoto: number
    sinPrecio: number
    sinStock: number
    pasarelasActivas: number
  }
  pasarelas: Array<{ key: string; name: string; mode: string; active: boolean }>
  sparkline: Array<{ d: string; total: number; count: number }>
  topProductos: Array<{ id: string; sku: string; nombre: string; cantidad: number; ingresos: number }>
  urgentes: Array<{ id: string; nrodePedido: string; cliente: string; total: number; diasEsperando: number }>
  recientes: Array<{
    id: string
    nrodePedido: string
    cliente: string
    total: number
    fp: string
    pagoStatus: string
    estado: string
    fecha: string
    provincia: string
  }>
  logs: Array<{
    id: string
    ts: string
    level: string
    category: string
    user: string
    action: string
    entity: string
    message: string
  }>
}

function statusBadge(s: string) {
  const low = (s || '').toLowerCase()
  if (low === 'delivered' || low.includes('entrega')) return <Badge variant="success">Entregado</Badge>
  if (low === 'shipped' || low.includes('envia')) return <Badge variant="info">Enviado</Badge>
  if (low === 'paid') return <Badge variant="success">Pagado</Badge>
  if (low === 'preparing' || low.includes('prepar')) return <Badge variant="warning">Preparando</Badge>
  if (low === 'pending') return <Badge variant="warning">Pendiente</Badge>
  if (low === 'cancelled' || low.includes('cancel')) return <Badge variant="danger">Cancelado</Badge>
  return <Badge>{s || 'Sin estado'}</Badge>
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function colorClasses(c: Color) {
  switch (c) {
    case 'emerald':
      return { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'bg-emerald-500 text-white' }
    case 'red':
      return { bg: 'bg-red-50', text: 'text-red-700', icon: 'bg-red-500 text-white' }
    case 'blue':
      return { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'bg-blue-500 text-white' }
    case 'violet':
      return { bg: 'bg-violet-50', text: 'text-violet-700', icon: 'bg-violet-500 text-white' }
    case 'amber':
      return { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'bg-amber-500 text-white' }
    case 'sky':
      return { bg: 'bg-sky-50', text: 'text-sky-700', icon: 'bg-sky-500 text-white' }
    case 'pink':
      return { bg: 'bg-pink-50', text: 'text-pink-700', icon: 'bg-pink-500 text-white' }
    case 'indigo':
      return { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'bg-indigo-500 text-white' }
  }
}

function KPI({
  label,
  value,
  hint,
  icon: Icon,
  color,
  onClick,
}: {
  label: string
  value: string | number
  hint?: string
  icon: typeof DollarSign
  color: Color
  onClick?: () => void
}) {
  const cls = colorClasses(color)
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`group rounded-2xl border border-ink-200 bg-white p-4 text-left transition-all ${onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md' : 'cursor-default'}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-ink-500">{label}</div>
          <div className="mt-1 text-2xl font-extrabold text-ink-900">{value}</div>
          {hint && <div className={`mt-0.5 text-[11px] ${cls.text}`}>{hint}</div>}
        </div>
        <div className={`grid size-9 place-items-center rounded-xl ${cls.icon}`}>
          <Icon className="size-4" />
        </div>
      </div>
    </button>
  )
}

function Sparkline({ data }: { data: Array<{ d: string; total: number }> }) {
  if (!data.length) return null
  const max = Math.max(...data.map((d) => d.total), 1)
  const W = 600
  const H = 80
  const stepX = W / (data.length - 1 || 1)
  const points = data.map((d, i) => {
    const x = i * stepX
    const y = H - (d.total / max) * (H - 8) - 4
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  const path = `M ${points.join(' L ')}`
  const area = `${path} L ${W},${H} L 0,${H} Z`
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-20 w-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E85230" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#E85230" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sparkGrad)" />
      <path d={path} fill="none" stroke="#E85230" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

function logLevelBadge(l: string) {
  if (l === 'error') return <Badge variant="danger">error</Badge>
  if (l === 'warn') return <Badge variant="warning">warn</Badge>
  return <Badge variant="info">info</Badge>
}

// ============================================================
// Analytics: Funnel de conversión + Top productos
// ============================================================

type RangeKey = 'today' | '7d' | '30d' | '90d'

interface FunnelData {
  from: string
  to: string
  product_views: number
  cart_adds: number
  checkout_starts: number
  checkout_payment_attempts: number
  checkout_success: number
  conversion: {
    view_to_cart: number
    cart_to_checkout: number
    checkout_to_payment: number
    payment_to_success: number
    overall: number
  }
}

interface TopProductsData {
  from: string
  to: string
  mostViewed: Array<{ id: string; sku: string; name: string; image: string | null; views: number }>
  mostSold: Array<{
    id: string
    sku: string
    name: string
    image: string | null
    units: number
    revenue: number
  }>
}

function rangeToDates(r: RangeKey): { from: string; to: string } {
  const today = new Date()
  const to = today.toISOString().slice(0, 10)
  if (r === 'today') return { from: to, to }
  const days = r === '7d' ? 7 : r === '30d' ? 30 : 90
  const f = new Date(today)
  f.setUTCDate(f.getUTCDate() - days + 1)
  return { from: f.toISOString().slice(0, 10), to }
}

function RangeSelector({
  value,
  onChange,
}: {
  value: RangeKey
  onChange: (v: RangeKey) => void
}) {
  const options: Array<{ k: RangeKey; label: string }> = [
    { k: 'today', label: 'Hoy' },
    { k: '7d', label: '7 días' },
    { k: '30d', label: '30 días' },
    { k: '90d', label: '90 días' },
  ]
  return (
    <div className="inline-flex rounded-lg border border-ink-200 bg-white p-0.5">
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          onClick={() => onChange(o.k)}
          className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
            value === o.k
              ? 'bg-brand-600 text-white'
              : 'text-ink-600 hover:bg-ink-50'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function FunnelStage({
  label,
  count,
  pct,
  widthPct,
  color,
}: {
  label: string
  count: number
  pct: number | null
  widthPct: number
  color: string
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-semibold text-ink-800">{label}</span>
        <span className="font-mono text-ink-500">
          {count.toLocaleString('es-MX')}
          {pct !== null && (
            <span className="ml-2 text-[11px] font-semibold text-emerald-700">
              {pct.toFixed(1)}%
            </span>
          )}
        </span>
      </div>
      <div className="h-7 w-full overflow-hidden rounded-md bg-ink-100">
        <div
          className="flex h-full items-center justify-end px-2 text-[10px] font-bold text-white transition-all"
          style={{
            width: `${Math.max(2, widthPct)}%`,
            background: color,
          }}
        >
          {widthPct >= 12 && `${widthPct.toFixed(0)}%`}
        </div>
      </div>
    </div>
  )
}

function FunnelSection({ range }: { range: RangeKey }) {
  const { from, to } = rangeToDates(range)
  const { data, isLoading, error } = useQuery<FunnelData>({
    queryKey: ['analytics-funnel', from, to],
    queryFn: () =>
      apiGet(`/api/admin/analytics/events-funnel?from=${from}&to=${to}`),
    refetchInterval: 120_000,
  })

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-ink-400">
        <Loader2 className="size-5 animate-spin" />
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="rounded-md bg-red-50 p-3 text-xs text-red-700">
        No se pudo cargar el funnel: {(error as Error)?.message || 'sin datos'}
      </div>
    )
  }

  const max = Math.max(
    data.product_views,
    data.cart_adds,
    data.checkout_starts,
    data.checkout_payment_attempts,
    data.checkout_success,
    1,
  )
  const wp = (n: number) => (n / max) * 100

  return (
    <div className="space-y-3">
      <FunnelStage
        label="1. Vistas de producto"
        count={data.product_views}
        pct={null}
        widthPct={wp(data.product_views)}
        color="linear-gradient(90deg,#3b82f6,#6366f1)"
      />
      <FunnelStage
        label="2. Agregados al carrito"
        count={data.cart_adds}
        pct={data.conversion.view_to_cart}
        widthPct={wp(data.cart_adds)}
        color="linear-gradient(90deg,#6366f1,#8b5cf6)"
      />
      <FunnelStage
        label="3. Checkout iniciado"
        count={data.checkout_starts}
        pct={data.conversion.cart_to_checkout}
        widthPct={wp(data.checkout_starts)}
        color="linear-gradient(90deg,#8b5cf6,#a855f7)"
      />
      <FunnelStage
        label="4. Intento de pago"
        count={data.checkout_payment_attempts}
        pct={data.conversion.checkout_to_payment}
        widthPct={wp(data.checkout_payment_attempts)}
        color="linear-gradient(90deg,#a855f7,#ec4899)"
      />
      <FunnelStage
        label="5. Orden creada"
        count={data.checkout_success}
        pct={data.conversion.payment_to_success}
        widthPct={wp(data.checkout_success)}
        color="linear-gradient(90deg,#ec4899,#E85230)"
      />

      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-ink-100 pt-3 text-center lg:grid-cols-5">
        <ConversionBox label="Vista → Carrito" value={data.conversion.view_to_cart} />
        <ConversionBox label="Carrito → Checkout" value={data.conversion.cart_to_checkout} />
        <ConversionBox label="Checkout → Pago" value={data.conversion.checkout_to_payment} />
        <ConversionBox label="Pago → Orden" value={data.conversion.payment_to_success} />
        <ConversionBox label="Conversión global" value={data.conversion.overall} highlight />
      </div>
    </div>
  )
}

function ConversionBox({
  label,
  value,
  highlight,
}: {
  label: string
  value: number
  highlight?: boolean
}) {
  return (
    <div
      className={`rounded-lg border p-2 ${
        highlight
          ? 'border-brand-200 bg-brand-50'
          : 'border-ink-100 bg-ink-50'
      }`}
    >
      <div
        className={`text-base font-extrabold ${
          highlight ? 'text-brand-700' : 'text-ink-900'
        }`}
      >
        {value.toFixed(1)}%
      </div>
      <div className="text-[10px] text-ink-500">{label}</div>
    </div>
  )
}

function TopProductsSection({ range }: { range: RangeKey }) {
  const { from, to } = rangeToDates(range)
  const { data, isLoading, error } = useQuery<TopProductsData>({
    queryKey: ['analytics-top-products', from, to],
    queryFn: () =>
      apiGet(`/api/admin/analytics/top-products?from=${from}&to=${to}`),
    refetchInterval: 120_000,
  })

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-ink-400">
        <Loader2 className="size-5 animate-spin" />
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="rounded-md bg-red-50 p-3 text-xs text-red-700">
        Sin datos: {(error as Error)?.message || 'sin datos'}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-600">
          <Eye className="size-3.5 text-sky-500" /> Más vistos
        </h3>
        {data.mostViewed.length === 0 ? (
          <p className="rounded-md bg-ink-50 p-3 text-xs text-ink-500">
            Sin vistas registradas en este rango.
          </p>
        ) : (
          <ol className="space-y-1">
            {data.mostViewed.map((p, i) => (
              <li
                key={p.id}
                className="flex items-center gap-2 rounded-md p-1.5 text-xs hover:bg-ink-50"
              >
                <span className="grid size-6 shrink-0 place-items-center rounded-md bg-sky-100 text-[10px] font-bold text-sky-700">
                  {i + 1}
                </span>
                <div className="flex-1 truncate">
                  <div className="truncate font-medium text-ink-900">{p.name}</div>
                  <div className="font-mono text-[10px] text-ink-500">{p.sku}</div>
                </div>
                <span className="text-[11px] font-bold text-sky-700">
                  {p.views.toLocaleString('es-MX')} <span className="text-ink-400">vistas</span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div>
        <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-600">
          <ShoppingBag className="size-3.5 text-emerald-500" /> Más vendidos
        </h3>
        {data.mostSold.length === 0 ? (
          <p className="rounded-md bg-ink-50 p-3 text-xs text-ink-500">
            Sin ventas registradas en este rango.
          </p>
        ) : (
          <ol className="space-y-1">
            {data.mostSold.map((p, i) => (
              <li
                key={p.id}
                className="flex items-center gap-2 rounded-md p-1.5 text-xs hover:bg-ink-50"
              >
                <span className="grid size-6 shrink-0 place-items-center rounded-md bg-emerald-100 text-[10px] font-bold text-emerald-700">
                  {i + 1}
                </span>
                <div className="flex-1 truncate">
                  <div className="truncate font-medium text-ink-900">{p.name}</div>
                  <div className="font-mono text-[10px] text-ink-500">
                    {p.sku} · {p.units.toLocaleString('es-MX')} u
                  </div>
                </div>
                <span className="text-[11px] font-bold text-emerald-700">
                  {formatMXN(p.revenue)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}

export function Dashboard() {
  const navigate = useNavigate()
  const [analyticsRange, setAnalyticsRange] = useState<RangeKey>('30d')
  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: () => apiGet('/api/admin/dashboard'),
    refetchInterval: 60_000,
  })

  return (
    <>
      <Topbar
        title="Dashboard"
        subtitle="KPIs en tiempo real · auto-refresh cada 60s"
        actions={
          <button
            onClick={() => navigate({ to: '/pedidos/$id', params: { id: 'nuevo' } })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nuevo pedido
          </button>
        }
      />
      <div className="space-y-6 p-6">
        {isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
            <p className="mt-3 text-sm">Cargando datos…</p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Error consultando la BD: {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            {/* === ALERTAS CRÍTICAS (solo si hay) === */}
            {(data.stats.pendientesUrgentes > 0 || data.stats.sinStock > 0) && (
              <div className="rounded-2xl border-2 border-red-200 bg-gradient-to-r from-red-50 to-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="grid size-10 shrink-0 place-items-center rounded-full bg-red-500 text-white">
                    <AlertTriangle className="size-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-red-900">Atención requerida</h3>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs">
                      {data.stats.pendientesUrgentes > 0 && (
                        <button
                          onClick={() => navigate({ to: '/pedidos', search: { status: 'pending' } as never })}
                          className="rounded-md bg-white px-2.5 py-1 font-semibold text-red-700 hover:bg-red-100"
                        >
                          {data.stats.pendientesUrgentes} pedidos pendientes &gt;24h →
                        </button>
                      )}
                      {data.stats.sinStock > 0 && (
                        <button
                          onClick={() => navigate({ to: '/reportes-calidad' })}
                          className="rounded-md bg-white px-2.5 py-1 font-semibold text-amber-700 hover:bg-amber-100"
                        >
                          {data.stats.sinStock.toLocaleString('es-MX')} productos sin stock →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* === KPIs HOY === */}
            <section>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-500">Hoy</h2>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <KPI
                  label="Ventas hoy"
                  value={formatMXN(data.stats.ventasHoy)}
                  hint={`${data.stats.pedidosHoy} pedidos`}
                  icon={DollarSign}
                  color="emerald"
                />
                <KPI
                  label="Pedidos pendientes"
                  value={data.stats.pendientes.toLocaleString('es-MX')}
                  hint={data.stats.pendientesUrgentes > 0 ? `${data.stats.pendientesUrgentes} urgentes` : 'todo OK'}
                  icon={Clock}
                  color={data.stats.pendientesUrgentes > 0 ? 'red' : 'amber'}
                  onClick={() => navigate({ to: '/pedidos' })}
                />
                <KPI
                  label="En preparación"
                  value={data.stats.preparando.toLocaleString('es-MX')}
                  hint="armando paquetes"
                  icon={Package}
                  color="blue"
                  onClick={() => navigate({ to: '/pedidos' })}
                />
                <KPI
                  label="Enviados hoy"
                  value={data.stats.enviadosHoy.toLocaleString('es-MX')}
                  hint="con guía generada"
                  icon={CheckCircle2}
                  color="indigo"
                />
              </div>
            </section>

            {/* === KPIs 30 DÍAS === */}
            <section>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-500">Últimos 30 días</h2>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <KPI
                  label="Ventas mes"
                  value={formatMXN(data.stats.ventas30)}
                  hint={`${data.stats.pedidos30} pedidos`}
                  icon={TrendingUp}
                  color="emerald"
                />
                <KPI
                  label="Ticket promedio"
                  value={formatMXN(data.stats.ticketPromedio)}
                  hint="por pedido pagado"
                  icon={ShoppingBag}
                  color="violet"
                />
                <KPI
                  label="Ventas 7 días"
                  value={formatMXN(data.stats.ventas7)}
                  hint={`${data.stats.pedidos7} pedidos`}
                  icon={DollarSign}
                  color="sky"
                />
                <KPI
                  label="Newsletter"
                  value={data.stats.newsletter.toLocaleString('es-MX')}
                  hint={`+${data.stats.newsletterLastWeek} esta semana`}
                  icon={Send}
                  color="pink"
                  onClick={() => navigate({ to: '/newsletter' })}
                />
              </div>
            </section>

            {/* === GRÁFICA VENTAS === */}
            <section className="rounded-2xl border border-ink-200 bg-white p-5">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-ink-900">Ventas últimos 30 días</h2>
                  <p className="text-xs text-ink-500">Solo pedidos pagados</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-extrabold text-brand-600">{formatMXN(data.stats.ventas30)}</div>
                  <div className="text-[11px] text-ink-500">{data.stats.pedidos30} pedidos · {formatMXN(data.stats.ticketPromedio)} ticket</div>
                </div>
              </div>
              <Sparkline data={data.sparkline} />
              <div className="mt-2 flex justify-between text-[10px] text-ink-400">
                <span>{data.sparkline[0]?.d.slice(5)}</span>
                <span>{data.sparkline[data.sparkline.length - 1]?.d.slice(5)}</span>
              </div>
            </section>

            {/* === ANALYTICS: Funnel + Top Productos === */}
            <section className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-500">
                  <Filter className="size-3.5" /> Analítica del journey
                </h2>
                <RangeSelector value={analyticsRange} onChange={setAnalyticsRange} />
              </div>

              {/* Funnel de conversión */}
              <div className="rounded-2xl border border-ink-200 bg-white p-5">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-ink-900">Funnel de conversión</h3>
                  <p className="text-[11px] text-ink-500">
                    Eventos del cliente desde ver un producto hasta crear una orden
                  </p>
                </div>
                <FunnelSection range={analyticsRange} />
              </div>

              {/* Top productos */}
              <div className="rounded-2xl border border-ink-200 bg-white p-5">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-ink-900">Productos: vistos vs vendidos</h3>
                  <p className="text-[11px] text-ink-500">
                    Top 10 en cada métrica para detectar oportunidades de conversión
                  </p>
                </div>
                <TopProductsSection range={analyticsRange} />
              </div>
            </section>

            {/* === GRID PRINCIPAL === */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Pedidos urgentes */}
              {data.urgentes.length > 0 && (
                <section className="lg:col-span-2">
                  <div className="overflow-hidden rounded-2xl border border-red-200 bg-white">
                    <header className="flex items-center justify-between border-b border-red-100 bg-red-50 px-5 py-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="size-4 text-red-600" />
                        <h2 className="text-sm font-semibold text-red-900">Pedidos urgentes (pendientes &gt;24h)</h2>
                      </div>
                      <button
                        onClick={() => navigate({ to: '/pedidos' })}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:text-red-900"
                      >
                        Ver todos <ArrowRight className="size-3" />
                      </button>
                    </header>
                    <table className="w-full text-sm">
                      <tbody>
                        {data.urgentes.map((o) => (
                          <tr
                            key={o.id}
                            onClick={() => navigate({ to: '/pedidos/$id', params: { id: o.id } })}
                            className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-red-50"
                          >
                            <td className="px-5 py-2.5">
                              <div className="font-mono text-sm font-semibold text-ink-900">#{o.nrodePedido}</div>
                              <div className="text-[10px] text-red-600">{o.diasEsperando} día{o.diasEsperando !== 1 ? 's' : ''} esperando</div>
                            </td>
                            <td className="px-5 py-2.5 text-ink-700">{o.cliente}</td>
                            <td className="px-5 py-2.5 text-right font-semibold text-ink-900">{formatMXN(o.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Pedidos recientes */}
              <section className={data.urgentes.length > 0 ? 'lg:col-span-3' : 'lg:col-span-2'}>
                <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
                  <header className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
                    <div>
                      <h2 className="text-sm font-semibold text-ink-900">Pedidos recientes</h2>
                      <p className="text-[11px] text-ink-500">Últimos 8</p>
                    </div>
                    <button
                      onClick={() => navigate({ to: '/pedidos' })}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700"
                    >
                      Ver todos <ArrowRight className="size-3" />
                    </button>
                  </header>
                  <table className="w-full text-sm">
                    <thead className="bg-ink-50">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-ink-500">
                        <th className="px-5 py-2 font-medium">Pedido</th>
                        <th className="px-5 py-2 font-medium">Cliente</th>
                        <th className="px-5 py-2 font-medium">Total</th>
                        <th className="px-5 py-2 font-medium">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recientes.map((p) => (
                        <tr
                          key={p.id}
                          onClick={() => navigate({ to: '/pedidos/$id', params: { id: p.id } })}
                          className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50"
                        >
                          <td className="px-5 py-2">
                            <div className="font-mono text-xs font-semibold text-ink-900">#{p.nrodePedido}</div>
                            <div className="text-[10px] text-ink-500">{formatDate(p.fecha)}</div>
                          </td>
                          <td className="px-5 py-2">
                            <div className="text-xs text-ink-900">{p.cliente}</div>
                            <div className="text-[10px] text-ink-500">{p.provincia}</div>
                          </td>
                          <td className="px-5 py-2 text-sm font-semibold text-ink-900">{formatMXN(p.total)}</td>
                          <td className="px-5 py-2">{statusBadge(p.estado)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Columna derecha: alertas catálogo + pasarelas + top productos + logs */}
              <aside className="space-y-4">
                {/* Alertas catálogo */}
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <AlertTriangle className="size-4 text-amber-500" /> Alertas catálogo
                  </h3>
                  <div className="space-y-2">
                    <AlertRow
                      icon={ImageOff}
                      label="Sin foto"
                      count={data.stats.sinFoto}
                      onClick={() => navigate({ to: '/reportes-calidad' })}
                    />
                    <AlertRow
                      icon={TagsIcon}
                      label="Sin precio"
                      count={data.stats.sinPrecio}
                      onClick={() => navigate({ to: '/reportes-calidad' })}
                    />
                    <AlertRow
                      icon={XCircle}
                      label="Sin stock"
                      count={data.stats.sinStock}
                      onClick={() => navigate({ to: '/reportes-calidad' })}
                    />
                  </div>
                </div>

                {/* Pasarelas REAL (de BD) */}
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <CreditCard className="size-4 text-emerald-500" /> Pasarelas de pago
                  </h3>
                  {data.pasarelas.length === 0 ? (
                    <p className="text-xs text-ink-500">No hay pasarelas configuradas</p>
                  ) : (
                    <div className="space-y-1.5">
                      {data.pasarelas.map((p) => (
                        <div key={p.key} className="flex items-center justify-between text-xs">
                          <span className="text-ink-700">{p.name}</span>
                          {p.active ? (
                            <Badge variant={p.mode === 'live' ? 'success' : 'warning'}>
                              ● {p.mode === 'live' ? 'Live' : 'Sandbox'}
                            </Badge>
                          ) : (
                            <Badge>Inactiva</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => navigate({ to: '/payment-methods' })}
                    className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-brand-600 hover:text-brand-700"
                  >
                    Configurar <ArrowRight className="size-3" />
                  </button>
                </div>

                {/* Top productos */}
                {data.topProductos.length > 0 && (
                  <div className="rounded-2xl border border-ink-200 bg-white p-5">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <TrendingUp className="size-4 text-violet-500" /> Top productos del mes
                    </h3>
                    <ol className="space-y-2">
                      {data.topProductos.map((p, i) => (
                        <li
                          key={p.id || p.sku}
                          onClick={() => p.id && navigate({ to: '/productos/$id', params: { id: p.id } })}
                          className={`flex items-center gap-2 text-xs ${p.id ? 'cursor-pointer hover:bg-ink-50' : ''} -mx-1 rounded p-1`}
                        >
                          <span className="grid size-6 shrink-0 place-items-center rounded-md bg-violet-100 text-[10px] font-bold text-violet-700">
                            {i + 1}
                          </span>
                          <div className="flex-1 truncate">
                            <div className="truncate font-medium text-ink-900">{p.nombre}</div>
                            <div className="font-mono text-[10px] text-ink-500">{p.sku} · {p.cantidad} vendidos</div>
                          </div>
                          <span className="text-[11px] font-semibold text-emerald-700">{formatMXN(p.ingresos)}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Últimos eventos sistema */}
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <Activity className="size-4 text-sky-500" /> Últimos eventos
                  </h3>
                  {data.logs.length === 0 ? (
                    <p className="text-xs text-ink-500">Sin eventos registrados</p>
                  ) : (
                    <div className="space-y-2">
                      {data.logs.map((l) => (
                        <div key={l.id} className="flex items-start gap-2 text-[11px]">
                          {logLevelBadge(l.level)}
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium text-ink-900">{l.action}</div>
                            <div className="truncate text-[10px] text-ink-500">
                              {l.user || l.category} · {formatDate(l.ts)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => navigate({ to: '/logs' })}
                    className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-brand-600 hover:text-brand-700"
                  >
                    Ver todos los logs <ArrowRight className="size-3" />
                  </button>
                </div>

                {/* Stats catálogo (sutil) */}
                <div className="rounded-2xl border border-ink-200 bg-gradient-to-br from-ink-50 to-white p-4 text-xs">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-base font-extrabold text-ink-900">{data.stats.productos.toLocaleString('es-MX')}</div>
                      <div className="text-[10px] text-ink-500">productos activos</div>
                    </div>
                    <div>
                      <div className="text-base font-extrabold text-ink-900">{data.stats.usuarios.toLocaleString('es-MX')}</div>
                      <div className="text-[10px] text-ink-500">clientes</div>
                    </div>
                    <div>
                      <div className="text-base font-extrabold text-ink-900">{data.stats.pedidos.toLocaleString('es-MX')}</div>
                      <div className="text-[10px] text-ink-500">pedidos totales</div>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </>
        )}
      </div>
    </>
  )
}

function AlertRow({
  icon: Icon,
  label,
  count,
  onClick,
}: {
  icon: typeof DollarSign
  label: string
  count: number
  onClick: () => void
}) {
  const isAlert = count > 0
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs transition-colors ${
        isAlert ? 'bg-amber-50 text-amber-900 hover:bg-amber-100' : 'text-ink-500 hover:bg-ink-50'
      }`}
    >
      <span className="flex items-center gap-2">
        <Icon className={`size-3.5 ${isAlert ? 'text-amber-600' : 'text-ink-400'}`} />
        {label}
      </span>
      <span className={`font-mono text-xs font-bold ${isAlert ? 'text-amber-900' : 'text-ink-400'}`}>
        {count.toLocaleString('es-MX')}
      </span>
    </button>
  )
}
